<template>
  <div
    class="cover-img-container"
    :class="[coverImgSize, coverImgRadius]"
    :style="{ 'background-image': backgroundImage ? backgroundImage : null }"
  >
    <div class="img-content">
      <div class="text-highlight">
        <div class="title">{{ content.title }}</div>
        <transition name="message">
          <div v-if="content.text" class="p-d-inline-flex p-ai-center message">
            {{ content.text }}
            <i class="pi" :class="content.icon" style="font-size: 2rem" />
          </div>
        </transition>
      </div>
      <div class="p-d-flex button-row" :class="buttonsPosition">
        <Button
          v-if="buttons.firstBtn"
          @click="firstBtnAction"
          :label="buttons.firstBtn"
          class="p-button-raised p-button-secondary button"
          :class="buttonOpacity"
        />
        <Button
          v-if="buttons.secondBtn"
          @click="secondBtnAction"
          :label="buttons.secondBtn"
          class="p-button-raised p-button-secondary button"
          :class="buttonOpacity"
        />
      </div>
      <transition name="spinner">
        <loading-spinner v-if="showSpinner" mode="contained"></loading-spinner>
      </transition>
    </div>
  </div>
</template>

<script>
import { computed } from "vue";
export default {
  props: [
    "style",
    "backgroundImage",
    "title",
    "content",
    "buttons",
    "showSpinner"
  ],
  emits: ["first-btn-action", "second-btn-action"],
  setup(props, context) {
    function firstBtnAction() {
      context.emit("first-btn-action");
    }

    function secondBtnAction() {
      context.emit("second-btn-action");
    }

    const coverImgRadius = computed(() => {
      return {
        "container-radius-sm": props.style.radius === "half",
        "container-radius-lg": props.style.radius === "full"
      };
    });

    const coverImgSize = computed(() => {
      return {
        "container-size-sm": props.style.size === "small",
        "container-size-md": props.style.size === "medium",
        "container-size-lg": props.style.size === "large",
        "container-size-keyboard": props.style.size === "keyboard"
      };
    });

    const buttonOpacity = computed(() => {
      return {
        "button-opaque":
          props.style.btnOpacity === "opaque" &&
          props.style.size !== "keyboard" &&
          !props.showSpinner,
        "button-transparent":
          props.style.btnOpacity === "transparent" ||
          props.style.size === "keyboard" ||
          props.showSpinner === true
      };
    });

    const buttonsPosition = computed(() => {
      return {
        "p-jc-between": props.buttons.firstBtn && props.buttons.secondBtn,
        "p-jc-end": props.buttons.secondBtn && !props.buttons.firstBtn,
        "p-jc-start": props.buttons.firstBtn && !props.buttons.secondBtn
      };
    });

    return {
      coverImgSize,
      coverImgRadius,
      buttonsPosition,
      buttonOpacity,
      firstBtnAction,
      secondBtnAction
    };
  }
};
</script>

<style lang="scss" scoped>
h3 {
  background-color: #fff;
}
.cover-img-container {
  margin: 2rem auto 0 auto;
  min-height: 150px;
  max-width: 40rem;
  background-repeat: no-repeat;
  background-size: 100% auto;
  box-shadow: 3px 0px 2px rgba(0, 0, 0, 0.5);
  background-repeat: no-repeat;
  background-size: cover;
  transition: height $animation-slow;

  .button-row {
    position: absolute;
    bottom: 1rem;
    left: 2rem;
    width: calc(100% - 4rem);
  }
}

.button-row {
  transition: all $animation-slow;
}

.container-size-sm {
  height: 20vh;
  transition: height $animation-slow;
}

.container-size-lg {
  height: 40vh;
  transition: height $animation-slow;
}

.container-radius-sm {
  border-radius: 12px 12px 0 0;
}

.container-radius-lg {
  border-radius: 12px;
}

i.pi {
  margin-left: 0.5rem;
  color: rgb(0, 153, 0);
}

.img-content {
  position: relative;
  height: 100%;
  width: 100%;
  padding: 2rem;

  .message {
    font-size: 1.5rem;
    font-weight: 500;
    padding: 0 1rem 0.5rem 1rem;
  }

  .title {
    font-size: 2rem;
    font-weight: 900;
    padding: 0 1rem;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .text-highlight {
    max-width: 100%;
    max-height: 100%;
    background: rgba(255, 255, 255, 0.7);
    border-radius: 12px;
    display: inline-block;
    overflow: hidden;
  }
}

.button {
  opacity: 0;
  transition: opacity $animation-fast;
}

.button-transparent {
  opacity: 0;
  pointer-events: none;
}

.button-opaque {
  opacity: 1;
}
/* ANIMATIONS */
.message-enter-from {
  opacity: 0;
  transform: translateY(-30px);
}
.message-enter-active,
.message-leave-active {
  transition: all $animation-fast;
}
.message-enter-to {
  opacity: 1;
  transform: translateY(0);
}
.message-leave-from {
  opacity: 1;
  transform: translateY(0);
}
.message-leave-to {
  opacity: 0;
  transform: translateY(-30px);
}

.spinner-leave-from,
.spinner-enter-to {
  opacity: 1;
}
.spinner-leave-to,
.spinner-enter-from {
  opacity: 0;
}
.spinner-leave-active,
.spinner-enter-active {
  transition: opacity $animation-fast;
}

@media only screen and (max-width: $screen-small) {
  .cover-img-container {
    position: sticky;
    min-height: auto;
    z-index: 10;
    top: 40px;
    left: 0;
    margin: 0;
    height: $cover-img-mobile-md;

    .button-row {
      width: calc(100% - 2rem);
      position: relative;
      left: auto;
    }
  }

  .img-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 0 2rem;
    transition: all $animation-slow-mobile;
  }

  .container-size-sm {
    height: calc(30vh - (#{$mobile-bar} / 2));
    transition: height $animation-slow-mobile;

    .button-row {
      top: 3rem;
    }
  }

  .container-size-keyboard {
    height: 60px;
    transition: height $animation-fast;

    .text-highlight {
      position: absolute;
    }

    .button-row {
      top: 3rem;
    }
  }

  .container-size-lg {
    height: calc(100vh - #{$mobile-bar});
    transition: height $animation-slow-mobile;

    .img-content {
      height: 60%;

      .button-row {
        top: 60px;
      }
    }
  }

  .container-radius-sm,
  .container-radius-lg {
    border-radius: 0;
  }
}
</style>
